import { Component, Input, Output, EventEmitter, computed, signal, AfterContentInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { TabItem, TabsVariant, TabsSize, TabsAlignment, TabsOrientation } from '../models/tabs.model';

export { TabItem, TabsVariant, TabsSize, TabsAlignment, TabsOrientation } from '../models/tabs.model';

const tabsContainerVariants = cva({
  base: [
    'relative backdrop-blur-sm border rounded-lg transition-all duration-300 ease-out',
    // Glass morphism base - minimal effect behind content
    'before:content-[""] before:absolute before:inset-0 before:z-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-10 before:rounded-lg before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        // Light theme - much more opaque for text contrast
        'bg-white/85 border-white/60',
        'shadow-lg shadow-black/10',
        // Dark theme - solid background for text contrast
        'dark:bg-gray-800/90 dark:border-gray-600/50',
        'dark:shadow-lg dark:shadow-black/30',
      ].join(' '),
      elevated: [
        // Light theme
        'bg-white/90 border-primary-200/70',
        'shadow-xl shadow-primary-500/15',
        // Dark theme
        'dark:bg-gray-800/95 dark:border-primary-400/60',
        'dark:shadow-xl dark:shadow-primary-500/25',
      ].join(' '),
      premium: [
        // Ultra-premium with strong contrast
        'bg-linear-to-br from-white/95 via-white/90 to-primary-50/80',
        'border-2 border-primary-300/80',
        'shadow-xl shadow-primary-500/20',
        // Dark theme
        'dark:bg-linear-to-br dark:from-gray-800/95 dark:via-gray-800/90 dark:to-primary-900/80',
        'dark:border-2 dark:border-primary-400/70',
        'dark:shadow-xl dark:shadow-primary-500/30',
      ].join(' '),
      minimal: [
        'bg-white/75 border-gray-200/50',
        'shadow-md shadow-gray-500/10',
        'dark:bg-gray-800/80 dark:border-gray-600/40',
        'dark:shadow-md dark:shadow-black/20',
      ].join(' '),
      pills: [
        'bg-white/85 border-white/50',
        'shadow-lg shadow-black/12',
        'dark:bg-gray-800/90 dark:border-gray-600/40',
        'dark:shadow-lg dark:shadow-black/30',
      ].join(' '),
    },
    orientation: {
      horizontal: 'flex flex-col',
      vertical: 'flex flex-row',
    },
  },
  defaultVariants: {
    variant: 'default',
    orientation: 'horizontal',
  },
});

const tabListVariants = cva({
  base: [
    'relative flex transition-all duration-300 ease-out z-10',
    'border-b border-white/25 dark:border-white/20',
  ].join(' '),
  variants: {
    variant: {
      default: 'bg-white/80 dark:bg-gray-700/85',
      elevated: 'bg-white/85 dark:bg-gray-700/90',
      premium: 'bg-linear-to-r from-white/90 to-primary-50/80 dark:from-gray-700/85 dark:to-primary-900/80',
      minimal: 'bg-white/70 dark:bg-gray-700/75',
      pills: 'bg-white/80 dark:bg-gray-700/85 p-2',
    },
    alignment: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      stretch: 'justify-stretch',
    },
    orientation: {
      horizontal: 'flex-row border-b',
      vertical: 'flex-col border-r min-w-[200px]',
    },
    size: {
      sm: 'min-h-[40px]',
      md: 'min-h-[48px]',
      lg: 'min-h-[56px]',
      xl: 'min-h-[64px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    alignment: 'start',
    orientation: 'horizontal',
    size: 'md',
  },
});

const tabButtonVariants = cva({
  base: [
    'relative flex items-center justify-center transition-all duration-300 ease-out z-20',
    'cursor-pointer border-none bg-transparent outline-none',
    'hover:bg-linear-to-b hover:from-white/70 hover:to-white/50',
    'dark:hover:bg-linear-to-b dark:hover:from-gray-600/70 dark:hover:to-gray-700/50',
    'focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2',
    'transform-gpu hover:scale-105 active:scale-95',
    // Disabled state
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
  ].join(' '),
  variants: {
    variant: {
      default: 'rounded-xl',
      elevated: 'rounded-xl',
      premium: 'rounded-xl',
      minimal: 'rounded-xl',
      pills: 'rounded-lg mx-1 first:ml-0 last:mr-0',
    },
    size: {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-6 py-4 text-lg',
      xl: 'px-8 py-5 text-xl',
    },
    active: {
      true: [
        'text-primary-900 dark:text-primary-100 font-bold',
        'bg-linear-to-b from-white/95 to-white/80',
        'dark:bg-linear-to-b dark:from-gray-600/95 dark:to-gray-700/80',
        'border border-primary-300/70 dark:border-primary-400/60',
        'shadow-lg shadow-primary-500/25',
      ].join(' '),
      false: 'text-gray-800 dark:text-gray-100 font-semibold hover:text-gray-900 dark:hover:text-white',
    },
    orientation: {
      horizontal: 'flex-1',
      vertical: 'w-full justify-start',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    active: 'false',
    orientation: 'horizontal',
  },
});

const tabIndicatorVariants = cva({
  base: ['absolute bg-primary-500 transition-all duration-300 ease-out', 'rounded-full'].join(' '),
  variants: {
    orientation: {
      horizontal: 'bottom-0 left-0 h-0.5',
      vertical: 'right-0 top-0 w-0.5',
    },
    variant: {
      default: 'bg-primary-500',
      elevated: 'bg-linear-to-r from-primary-500 to-primary-600',
      premium: 'bg-linear-to-r from-primary-500 via-primary-600 to-primary-700 shadow-lg shadow-primary-500/30',
      minimal: 'bg-primary-400',
      pills: 'hidden', // Pills use background color instead
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    variant: 'default',
  },
});

const contentVariants = cva({
  base: ['relative z-20 flex-1 transition-all duration-300 ease-out', 'overflow-hidden'].join(' '),
  variants: {
    size: {
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
      xl: 'p-10',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

@Component({
  selector: 'ui-tabs-glass',
  standalone: true,
  imports: [CommonModule, IconGlassComponent],
  templateUrl: './tabs-glass.component.html',
})
export class TabsGlassComponent implements AfterContentInit {
  // ThemeService intentionally not injected here to avoid unused variable warning.

  @Input() tabs: TabItem[] | null | undefined = [];
  @Input() variant: TabsVariant = 'default';
  @Input() size: TabsSize = 'md';
  @Input() alignment: TabsAlignment = 'start';
  @Input() orientation: TabsOrientation = 'horizontal';
  @Input() defaultTab: string = '';
  @Input() themeSection: ThemeSection = 'global';

  @Output() tabChange = new EventEmitter<string>();
  @Output() tabClick = new EventEmitter<{ tab: TabItem; index: number }>();

  protected activeTab = signal<string>('');

  ngAfterContentInit(): void {
    // Set initial active tab
    const tabsArr = Array.isArray(this.tabs) ? this.tabs : [];
    if (this.defaultTab) {
      this.activeTab.set(this.defaultTab);
    } else if (tabsArr.length > 0) {
      const firstEnabledTab = tabsArr.find((tab) => !tab.disabled);
      // eslint-disable-next-line eqeqeq
      if (firstEnabledTab && firstEnabledTab.value != null) {
        this.activeTab.set(firstEnabledTab.value);
      }
    }
  }

  protected iconSize = computed(() => {
    const sizeMap = {
      sm: 'sm' as const,
      md: 'sm' as const,
      lg: 'md' as const,
      xl: 'lg' as const,
    };
    return sizeMap[this.size];
  });

  protected computedContainerClasses = computed(() => {
    return tabsContainerVariants({
      variant: this.variant,
      orientation: this.orientation,
    });
  });

  protected computedTabListClasses = computed(() => {
    return tabListVariants({
      variant: this.variant,
      alignment: this.alignment,
      orientation: this.orientation,
      size: this.size,
    });
  });

  protected getTabButtonClasses(tabValue: string): string {
    if (!tabValue) {
      return '';
    }
    return tabButtonVariants({
      variant: this.variant,
      size: this.size,
      active: this.isActiveTab(tabValue) ? 'true' : 'false',
      orientation: this.orientation,
    });
  }

  protected computedIndicatorClasses = computed(() => {
    return tabIndicatorVariants({
      orientation: this.orientation,
      variant: this.variant,
    });
  });

  protected computedContentClasses = computed(() => {
    return contentVariants({
      size: this.size,
    });
  });

  protected indicatorStyle = computed(() => {
    const tabsArr = Array.isArray(this.tabs) ? this.tabs : [];
    const activeIndex = tabsArr.findIndex((tab) => tab.value === this.activeTab());
    if (activeIndex === -1) return {} as Record<string, string>;

    const tabCount = tabsArr.length;

    if (this.orientation === 'horizontal') {
      const width = tabCount > 0 ? 100 / tabCount : 0;
      const left = activeIndex * width;
      return {
        width: `${width}%`,
        left: `${left}%`,
      } as Record<string, string>;
    } else {
      const height = tabCount > 0 ? 100 / tabCount : 0;
      const top = activeIndex * height;
      return {
        height: `${height}%`,
        top: `${top}%`,
      } as Record<string, string>;
    }
  });

  protected isActiveTab(tabValue: string): boolean {
    const tabsArr = Array.isArray(this.tabs) ? this.tabs : [];
    if (!tabsArr.length) return false;
    return this.activeTab() === tabValue;
  }

  protected selectTab(tabValue: string): void {
    const tabsArr = Array.isArray(this.tabs) ? this.tabs : [];
    const tab = tabsArr.find((t) => t.value === tabValue);
    if (!tab || tab.disabled) return;

    this.activeTab.set(tabValue);
    this.tabChange.emit(tabValue);

    const index = tabsArr.findIndex((t) => t.value === tabValue);
    this.tabClick.emit({ tab, index });
  }

  protected handleTabKeyDown(event: KeyboardEvent, currentIndex: number): void {
    const tabsArr = Array.isArray(this.tabs) ? this.tabs : [];
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = this.getNextEnabledTabIndex(currentIndex, 1, tabsArr);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = this.getNextEnabledTabIndex(currentIndex, -1, tabsArr);
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = this.getNextEnabledTabIndex(-1, 1, tabsArr);
        break;
      case 'End':
        event.preventDefault();
        nextIndex = this.getNextEnabledTabIndex(tabsArr.length, -1, tabsArr);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        // Guard the value which may be undefined on TabItem
        this.selectTab(tabsArr[currentIndex]?.value ?? '');
        return;
    }

    if (nextIndex !== currentIndex) {
      this.selectTab(tabsArr[nextIndex]?.value ?? '');
      // Focus the new tab
      setTimeout(() => {
        const tabButton = document.getElementById(`tab-${tabsArr[nextIndex]?.value ?? ''}`);
        tabButton?.focus();
      });
    }
  }

  private getNextEnabledTabIndex(currentIndex: number, direction: number, tabsArr: TabItem[]): number {
    let nextIndex = currentIndex + direction;

    while (nextIndex >= 0 && nextIndex < tabsArr.length) {
      const maybeTab = tabsArr[nextIndex];
      if (maybeTab && !maybeTab.disabled) {
        return nextIndex;
      }
      nextIndex += direction;
    }

    // Wrap around
    if (direction > 0) {
      for (let i = 0; i < currentIndex; i++) {
        const maybeTab = tabsArr[i];
        if (maybeTab && !maybeTab.disabled) {
          return i;
        }
      }
    } else {
      for (let i = tabsArr.length - 1; i > currentIndex; i--) {
        const maybeTab = tabsArr[i];
        if (maybeTab && !maybeTab.disabled) {
          return i;
        }
      }
    }

    return currentIndex;
  }
}

/**
 * Usage Examples:
 *
 * Basic e-commerce product tabs:
 * <ui-tabs-glass
 *   [tabs]="productTabs"
 *   variant="premium"
 *   size="lg"
 *   defaultTab="description"
 * >
 *   <div data-tab="description">
 *     <h3>Product Description</h3>
 *     <p>Detailed product information...</p>
 *   </div>
 *   <div data-tab="reviews">
 *     <h3>Customer Reviews</h3>
 *     <!-- Review components -->
 *   </div>
 *   <div data-tab="shipping">
 *     <h3>Shipping Information</h3>
 *     <!-- Shipping details -->
 *   </div>
 * </ui-tabs-glass>
 *
 * Admin dashboard tabs:
 * <ui-tabs-glass
 *   [tabs]="dashboardTabs"
 *   variant="elevated"
 *   orientation="vertical"
 *   size="md"
 *   (tabChange)="onTabChange($event)"
 * >
 *   <div data-tab="analytics">Analytics Dashboard</div>
 *   <div data-tab="users">User Management</div>
 *   <div data-tab="settings">System Settings</div>
 * </ui-tabs-glass>
 *
 * Pills variant:
 * <ui-tabs-glass
 *   [tabs]="categoryTabs"
 *   variant="pills"
 *   alignment="center"
 * >
 *   <div data-tab="all">All Products</div>
 *   <div data-tab="electronics">Electronics</div>
 *   <div data-tab="clothing">Clothing</div>
 * </ui-tabs-glass>
 *
 * Tab configuration:
 * productTabs: TabItem[] = [
 *   {
 *     label: 'Description',
 *     value: 'description',
 *     icon: 'file-text'
 *   },
 *   {
 *     label: 'Reviews',
 *     value: 'reviews',
 *     icon: 'star',
 *     badge: 127
 *   },
 *   {
 *     label: 'Shipping',
 *     value: 'shipping',
 *     icon: 'truck'
 *   },
 *   {
 *     label: 'Return Policy',
 *     value: 'returns',
 *     icon: 'rotate-ccw',
 *     disabled: true
 *   }
 * ];
 *
 * Different variants:
 * <ui-tabs-glass variant="default" [tabs]="tabs"></ui-tabs-glass>
 * <ui-tabs-glass variant="elevated" [tabs]="tabs"></ui-tabs-glass>
 * <ui-tabs-glass variant="premium" [tabs]="tabs"></ui-tabs-glass>
 * <ui-tabs-glass variant="minimal" [tabs]="tabs"></ui-tabs-glass>
 * <ui-tabs-glass variant="pills" [tabs]="tabs"></ui-tabs-glass>
 */
